/**
 * menus/shared.jsx — Shared primitives for all MenuBar sub-menus.
 *
 * Exports:
 *   useIsGuidanceMode — true when promptMode is 'guided' or 'light'
 *   MenuItem         — thin wrapper around DropdownMenuItem (accepts optional `tip`)
 *   GatedMenuItem    — capability-gated MenuItem (shows lock + upgrade toast)
 *   Menu             — top-level trigger + content wrapper
 *   ParchmentTabIcon — inline SVG used by the Story Setup menu trigger
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useCapabilities } from '@/lib/useCapabilities';
import { Lock, LockOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getPromptMode } from '@/components/OnboardingPromptModeDialog';

/** Returns true when the user has chosen Guided or Light (Some Guidance) mode. */
export function useIsGuidanceMode() {
  const [mode, setMode] = React.useState(getPromptMode);
  React.useEffect(() => {
    const handler = (e) => setMode(e.detail?.mode || getPromptMode());
    window.addEventListener('loom:prompt-mode-changed', handler);
    return () => window.removeEventListener('loom:prompt-mode-changed', handler);
  }, []);
  return mode === 'guided' || mode === 'light';
}

/**
 * MenuItem — thin wrapper around DropdownMenuItem.
 * In Guided / Light mode, accepts an optional `tip` string that renders as a
 * one-line description below the label so users know why they'd use this item.
 */
export const MenuItem = ({ children, tip, ...p }) => {
  const isGuidance = useIsGuidanceMode();
  return (
    <DropdownMenuItem
      className={`text-[12.5px] gap-2 cursor-pointer ${isGuidance && tip ? 'flex-col items-start gap-0 py-1.5' : ''}`}
      {...p}
    >
      <div className="flex w-full items-center gap-2">{children}</div>
      {isGuidance && tip && (
        <p className="mt-0.5 pl-[22px] text-[10.5px] leading-snug text-muted-foreground/75 font-normal">
          {tip}
        </p>
      )}
    </DropdownMenuItem>
  );
};

/* iter55g — Capability-gated menu item.
 * Renders like MenuItem when the user has the required capability.
 * When they don't, shows greyed-out with a lock icon and a toast CTA. */
export function GatedMenuItem({ capability, onClick, children, tip, ...p }) {
  const { can, upgradeCta } = useCapabilities();
  const isGuidance = useIsGuidanceMode();
  const allowed = can(capability);
  const handle = (e) => {
    if (allowed) return onClick?.(e);
    e.preventDefault();
    const cta = upgradeCta(capability);
    toast.info(cta.title, {
      description: `${cta.body} · Upgrade in Settings → Subscription.`,
      duration: 4500,
    });
  };
  return (
    <DropdownMenuItem
      onClick={handle}
      className={`text-[12.5px] gap-2 cursor-pointer ${allowed ? '' : 'opacity-60 hover:!bg-transparent'} ${isGuidance && tip ? 'flex-col items-start gap-0 py-1.5' : ''}`}
      {...p}
    >
      <div className="flex w-full items-center gap-2">
        {children}
        {allowed  && <LockOpen className="ml-auto h-3 w-3 text-emerald-400/70" data-testid="menu-unlock-icon" />}
        {!allowed && <Lock     className="ml-auto h-3 w-3 text-muted-foreground" data-testid="menu-lock-icon" />}
      </div>
      {isGuidance && tip && (
        <p className="mt-0.5 pl-[22px] text-[10.5px] leading-snug text-muted-foreground/75 font-normal">
          {tip}
        </p>
      )}
    </DropdownMenuItem>
  );
}

/* iter55p8 — Parchment-tab glyph used as the Story Setup menu icon. */
export function ParchmentTabIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d="M4 3.5 L14 3.5 L17 6.5 L17 16.2 A0.6 0.6 0 0 1 16.1 16.7 L13.5 15.2 L11.5 16.4 L9.5 15.2 L7.5 16.4 L5.5 15.2 L3.9 16.7 A0.6 0.6 0 0 1 3 16.2 L3 4.5 A1 1 0 0 1 4 3.5 Z"
        fill="currentColor" opacity="0.16"
      />
      <path
        d="M4 3.5 L14 3.5 L17 6.5 L17 16.2 A0.6 0.6 0 0 1 16.1 16.7 L13.5 15.2 L11.5 16.4 L9.5 15.2 L7.5 16.4 L5.5 15.2 L3.9 16.7 A0.6 0.6 0 0 1 3 16.2 L3 4.5 A1 1 0 0 1 4 3.5 Z"
        fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"
      />
      <path d="M14 3.5 L14 6.5 L17 6.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

/* Top-level dropdown trigger + content wrapper.
 * onOpen       — optional callback fired when the menu opens
 * triggerClassName — extra classes applied to the trigger button (e.g. glow ring)
 */
export function Menu({ label, children, icon: Icon = null, onOpen, triggerClassName = '' }) {
  const testid = `menubar-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <DropdownMenu onOpenChange={(open) => { if (open) onOpen?.(); }}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testid}
          className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-[12.5px] text-chrome-foreground/90 hover:bg-secondary data-[state=open]:bg-secondary ${triggerClassName}`}
        >
          {Icon && <Icon className="h-3.5 w-3.5 opacity-80" aria-hidden />}
          {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px] text-[12.5px]">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

